import React from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePath } from 'react-router';
import pages from 'pages';
import Header from 'shared/components/Header/Header';
import Text from 'shared/components/Text/Text';
import { AgreementCard } from 'modules/support/components/AgreementCard/AgreementCard';
import { VideoResourcesCard } from 'modules/support/components/VideoResourcesCard/VideoResourcesCard';
import { SupportContactCard } from 'modules/support/components/SupportContactCard/SupportContactCard';
import { Button } from '@sighten/beacon';
import { useUser } from 'hooks/useUser';
import { useDownloadContract } from 'api/agreement';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import {
  AgreementTransferWrapper,
  ContentWrapper,
  MainContent,
  Sidebar,
  InfoList,
  OptionItem,
  Steps,
  StepTitle,
  OptionsList,
  StepLink,
  LoadingContainer,
} from './AgreementTransferFAQ.styles';

export function AgreementTransferFAQ() {
  const { selectedAgreement, userResult } = useUser();
  const isLoading = !userResult || userResult.loading;
  const contractType: 'ppa' | 'ric' = selectedAgreement?.is_ric ? 'ric' : 'ppa';

  const { t } = useTranslation('translation', {
    keyPrefix: 'support&FAQs.faqCategories.agreementTransfer',
  });

  const transferOptions = selectedAgreement
    ? Object.entries(
        t('options.choices', { returnObjects: true }) as Record<
          string,
          {
            title: string;
            description: string;
          }
        >,
      )
        .filter(([key]) => {
          if (key.includes('_')) {
            return key.includes(contractType);
          }
          return !(key === 'prepay' && contractType === 'ric');
        })
        .map(([_key, value]) => value)
    : [];

  return (
    <AgreementTransferWrapper>
      <Header
        pageName="AGREEMENT_TRANSFER"
        title={t('pageTitle')}
        routeBreadCrumbs={[
          {
            label: t('support', { keyPrefix: 'support&FAQs' }),
            link: resolvePath(pages.SUPPORT),
          },
          { label: t('pageTitle') },
        ]}
      />

      <ContentWrapper>
        <MainContent>
          {isLoading || !selectedAgreement ? (
            <LoadingContainer>
              <LoadingSpinner />
            </LoadingContainer>
          ) : (
            <>
              <section>
                <Text as="h4">{t('intro.title')}</Text>
                <Text as="p">{t('intro.body')}</Text>
              </section>
              <Button as="a" href="#" kind="secondary">
                {t('startBtn')}
              </Button>
              <section>
                <Text as="h4">{t('options.title')}</Text>
                <Text as="p">{t('options.body')}</Text>
                <OptionsList>
                  {transferOptions.map((option) => (
                    <OptionItem key={option.title}>
                      <Text as="h4">{option.title}</Text>
                      <Text as="p">{option.description}</Text>
                    </OptionItem>
                  ))}
                </OptionsList>
              </section>

              <section>
                <Text as="h4">{t('information.title')}</Text>
                <InfoList>
                  {(t('information.list', { returnObjects: true }) as string[]).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </InfoList>
              </section>

              <section>
                <Text as="h4">{t('steps.title')}</Text>
                <Steps>
                  {(
                    t('steps.list', { returnObjects: true }) as Array<{
                      title: string;
                      description: string;
                    }>
                  ).map((step) => (
                    <li key={step.title}>
                      <StepTitle>{step.title}</StepTitle>
                      <Text as="p">
                        {step.description.includes('<a>') ? (
                          <StepLink to="#">{step.description.replace(/<a>|<\/a>/g, '')}</StepLink>
                        ) : (
                          step.description
                        )}
                      </Text>
                    </li>
                  ))}
                </Steps>
              </section>

              <section>
                <Text as="h4">{t('progress.title')}</Text>
                <Text as="p">{t('progress.body')}</Text>
                <br />
                <Text as="p">{t('progress.footnote')}</Text>
              </section>
            </>
          )}
        </MainContent>

        <Sidebar>
          <VideoResourcesCard />
          {selectedAgreement && (
            <AgreementCard
              agreementSigned={selectedAgreement.contract_signed_date}
              // Asserting number here - if the agreement loads this is set
              agreementTerm={selectedAgreement.contract_term as number}
              agreementId={selectedAgreement.id}
            />
          )}
          <SupportContactCard />
        </Sidebar>
      </ContentWrapper>
    </AgreementTransferWrapper>
  );
}

export default AgreementTransferFAQ;
